import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import OolaWheelMilestoneV2 from '../OolaWheelMilestoneV2';
import { readableFColor } from '../../../../util/readableBrandColor';
import './ArrangeSevenV2.css';

function formatDate(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function goalKey(goal, index) {
    if (goal?.GoalId != null) return `g-${goal.GoalId}`;
    if (goal?.idL) return `g-${goal.idL}`;
    if (goal?.id) return `g-${goal.id}`;
    return `g-${goal?.areaId ?? 'x'}-${goal?.recomendationIndex ?? index}`;
}

/**
 * Portal V2 — Arrange the Top 7 (order only).
 * Oola One is chosen in the next step — not here.
 * Same data as classic: ONLY SelectedGoalsSeven.
 */
export default function ArrangeSevenV2({
    AllState,
    changeData,
    onContinue,
}) {
    const seven = (AllState.SelectedGoalsSeven || [])
        .filter(Boolean)
        .slice(0, 7);
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);
    const [grabIndex, setGrabIndex] = useState(null);
    const dragIndexRef = useRef(null);
    const grabTimerRef = useRef(null);

    const isDesktopSort = () =>
        typeof window !== 'undefined' &&
        window.matchMedia('(min-width: 768px)').matches;

    const clearGrab = () => {
        if (grabTimerRef.current != null) {
            window.clearTimeout(grabTimerRef.current);
            grabTimerRef.current = null;
        }
        setGrabIndex(null);
    };

    useEffect(() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        return () => clearGrab();
    }, []);

    const persistOrder = (nextSeven) => {
        changeData({
            ...AllState,
            SelectedGoalsSeven: nextSeven,
        });
    };

    const reorder = (from, to) => {
        if (from == null || to == null || from === to) return;
        if (from < 0 || to < 0 || from >= seven.length || to >= seven.length) {
            return;
        }
        const next = [...seven];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        persistOrder(next);
    };

    const moveBy = (index, delta) => {
        reorder(index, index + delta);
    };

    const RankControls = ({ index }) => (
        <div
            className="pv2-arrange7__arrows"
            draggable={false}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <button
                type="button"
                className="pv2-arrange7__arrow"
                draggable={false}
                disabled={index <= 0}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveBy(index, -1);
                }}
                aria-label="Move up"
            >
                <svg viewBox="0 0 12 8" aria-hidden="true">
                    <path
                        d="M1 6.5 L6 1.5 L11 6.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
            <button
                type="button"
                className="pv2-arrange7__arrow"
                draggable={false}
                disabled={index >= seven.length - 1}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveBy(index, 1);
                }}
                aria-label="Move down"
            >
                <svg viewBox="0 0 12 8" aria-hidden="true">
                    <path
                        d="M1 1.5 L6 6.5 L11 1.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
        </div>
    );

    const onMouseDown = (e, index) => {
        if (!isDesktopSort()) return;
        if (e.target.closest('.pv2-arrange7__arrows')) return;
        clearGrab();
        grabTimerRef.current = window.setTimeout(() => {
            setGrabIndex(index);
            grabTimerRef.current = null;
        }, 110);
    };

    const onMouseUp = () => {
        if (!isDesktopSort()) return;
        clearGrab();
    };

    const onDragStart = (e, index) => {
        clearGrab();
        dragIndexRef.current = index;
        setDragIndex(index);
        setOverIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
    };

    const onDragEnd = (e) => {
        clearGrab();
        const from = dragIndexRef.current;
        const to = overIndex;
        dragIndexRef.current = null;
        setDragIndex(null);
        setOverIndex(null);
        if (from != null && to != null) reorder(from, to);
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (overIndex !== index) setOverIndex(index);
    };

    const onDrop = (e, index) => {
        e.preventDefault();
        const from = dragIndexRef.current;
        dragIndexRef.current = null;
        setDragIndex(null);
        setOverIndex(null);
        if (from != null) reorder(from, index);
    };

    const onTouchStart = (e, index) => {
        if (
            typeof window !== 'undefined' &&
            !window.matchMedia('(max-width: 767px)').matches
        ) {
            return;
        }
        dragIndexRef.current = index;
        setDragIndex(index);
        setOverIndex(index);
    };

    const onTouchMove = (e) => {
        if (dragIndexRef.current == null) return;
        if (
            typeof window !== 'undefined' &&
            !window.matchMedia('(max-width: 767px)').matches
        ) {
            return;
        }
        const touch = e.touches?.[0];
        if (!touch) return;
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const card = target?.closest?.('[data-arrange-index]');
        if (!card) return;
        const idx = Number(card.getAttribute('data-arrange-index'));
        if (!Number.isNaN(idx) && idx !== overIndex) setOverIndex(idx);
    };

    const onTouchEnd = () => {
        if (
            typeof window !== 'undefined' &&
            !window.matchMedia('(max-width: 767px)').matches
        ) {
            return;
        }
        const from = dragIndexRef.current;
        const to = overIndex;
        dragIndexRef.current = null;
        setDragIndex(null);
        setOverIndex(null);
        if (from != null && to != null) reorder(from, to);
    };

    const ready = seven.length === 7;

    return (
        <section className="pv2-arrange7">
            <aside className="pv2-arrange7__side">
                <div className="pv2-arrange7__wheel">
                    <OolaWheelMilestoneV2
                        Fs={AllState.Fs || []}
                        activeIndex={-1}
                        hideFooter
                    />
                </div>
            </aside>

            <div className="pv2-arrange7__panel">
                <header className="pv2-arrange7__head">
                    <div className="pv2-arrange7__titles">
                        <p className="pv2-arrange7__eyebrow">Step 2 · Arrange</p>
                        <h2 className="pv2-arrange7__title">
                            Prioritize your Top 7
                        </h2>
                        <p className="pv2-arrange7__lead">
                            Put your goals in the order that feels most
                            important for your life right now.
                        </p>
                    </div>
                </header>

                {seven.length === 0 ? (
                    <p className="pv2-arrange7__empty">
                        No Top 7 picks yet. Go back to Pick your Top 7 and
                        select seven goals first.
                    </p>
                ) : (
                    <>
                        <p className="pv2-arrange7__howto pv2-arrange7__howto--desk">
                            Drag a goal or use the arrows to reorder.
                        </p>
                        <p className="pv2-arrange7__howto pv2-arrange7__howto--mobile">
                            Tap a goal, hold until you feel a click or buzz,
                            then drag to reorder.
                        </p>
                    </>
                )}

                <ol
                    className={`pv2-arrange7__list${
                        dragIndex != null ? ' is-sorting' : ''
                    }`}
                >
                    {seven.map((item, index) => {
                        const f = AllState.Fs?.[item.areaId] || {};
                        const accent = readableFColor(
                            f.name,
                            f.color || '#15c285'
                        );
                        const isOver =
                            overIndex === index &&
                            dragIndex != null &&
                            dragIndex !== index;
                        return (
                            <li
                                key={goalKey(item, index)}
                                data-arrange-index={index}
                                className={`pv2-arrange7__card${
                                    dragIndex === index ? ' is-dragging' : ''
                                }${
                                    grabIndex === index && dragIndex == null
                                        ? ' is-grab-pending'
                                        : ''
                                }${isOver ? ' is-over' : ''}`}
                                style={{ ['--pv2-arr-accent']: accent }}
                                draggable
                                onMouseDown={(e) => onMouseDown(e, index)}
                                onMouseUp={onMouseUp}
                                onMouseLeave={onMouseUp}
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragEnd={onDragEnd}
                                onDragOver={(e) => onDragOver(e, index)}
                                onDrop={(e) => onDrop(e, index)}
                                onDragEnter={(e) => {
                                    e.preventDefault();
                                    setOverIndex(index);
                                }}
                                onTouchStart={(e) => onTouchStart(e, index)}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                            >
                                <span
                                    className="pv2-arrange7__rank"
                                    aria-label={`Order ${index + 1}`}
                                >
                                    {index + 1}
                                </span>
                                <div className="pv2-arrange7__body">
                                    <div className="pv2-arrange7__meta">
                                        <span className="pv2-arrange7__f">
                                            {f.name || 'Goal'}
                                        </span>
                                        {item.date ? (
                                            <span className="pv2-arrange7__date">
                                                {formatDate(item.date)}
                                            </span>
                                        ) : null}
                                    </div>
                                    <span className="pv2-arrange7__text">
                                        {item.text}
                                    </span>
                                </div>
                                <RankControls index={index} />
                            </li>
                        );
                    })}
                </ol>

                <div className="pv2-arrange7__actions">
                    <button
                        type="button"
                        className={`pv2-arrange7__cta${
                            ready ? ' is-ready' : ' is-disabled'
                        }`}
                        disabled={!ready}
                        onClick={onContinue}
                    >
                        Continue to Actions
                    </button>
                </div>
            </div>

            {typeof document !== 'undefined'
                ? createPortal(
                      <div
                          className={`pv2-arrange7__dock${
                              ready ? ' is-ready' : ''
                          }`}
                          aria-label="Continue to next step"
                      >
                          <button
                              type="button"
                              className={`pv2-arrange7__dock-cta${
                                  ready ? ' is-ready' : ' is-disabled'
                              }`}
                              disabled={!ready}
                              onClick={onContinue}
                          >
                              Continue to Actions
                          </button>
                      </div>,
                      document.body
                  )
                : null}
        </section>
    );
}
